import { IBufferControllerConfig } from '@bufferController/BufferController.config';
import { BufferControllerEvents } from '@bufferController/BufferController.events';
import { ConfigConsumer } from '@configProvider/ConfigConsumer';
import { EErrorType, EEvent } from '@customTypes/events';
import { TEventBus } from '@eventBus/EventBus';
import { Logger, TLogger } from '@logger/Logger';
import bind from '@utils/decorators/bind';

export type TBufferOperation = (sourceBuffer: SourceBuffer) => void;
export type TErrorHandler = (sourceBuffer: SourceBuffer, error: Error) => void;
export type TOpItem = { operation: TBufferOperation; errorHandler?: TErrorHandler };

export class BufferOpsQueue extends ConfigConsumer<IBufferControllerConfig> {
  public _eventBus: TEventBus;
  private _sourceBuffer: SourceBuffer | null = null;
  private _opsQueue: Array<TOpItem> = [];
  private _sourceBufferStuckTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly _logger: TLogger;

  constructor(eventBus: TEventBus) {
    super('bufferController', eventBus);
    this._logger = Logger(eventBus, 'Doppio - BufferOpsQueue');

    this._eventBus = eventBus;
  }

  public setSourceBuffer(sourceBuffer: SourceBuffer): void {
    if (this._sourceBuffer) {
      this._sourceBuffer.removeEventListener('updateend', this._onBufferReady);
    }
    this._sourceBuffer = sourceBuffer;
    this._sourceBuffer.addEventListener('updateend', this._onBufferReady);

    this._sourceBufferStuckTimer = setTimeout(() => {
      this._eventBus.publish(BufferControllerEvents.sourceBufferStuck, {});
    }, this.config.sourceBufferStuckTimeout);
  }

  public destroy(): void {
    super.destroy();

    try {
      this._disarmSourceBufferStuckTimer();

      this._sourceBuffer?.removeEventListener('updateend', this._onBufferReady);
      this._sourceBuffer?.abort();
    } catch (_e) {
      // Ignore buffer error due to destroying;
      this._logger.info('Source buffer cannot be aborted. Ignore it');
    }

    this._opsQueue = [];
  }

  public removeSourceBuffer(parentMediaSource: MediaSource | null): void {
    if (this._sourceBuffer) {
      this.immediatelyRecreateSourceBuffer(parentMediaSource);
      this._sourceBuffer.removeEventListener('updateend', this._onBufferReady);
      this._sourceBuffer = null;
    }
  }

  public addBufferOperation(operation: TBufferOperation, errorHandler?: TErrorHandler, label?: string): void {
    if (!this._sourceBuffer) {
      this._logger.warn(
        `There is no sourceBuffer defined. Operation (${label || 'unknown'}) will be executed once sourceBuffer will be appended`,
      );
      this._opsQueue.push({ operation, errorHandler });
      return;
    }

    if (this._sourceBuffer.updating || this._opsQueue.length > 0) {
      this._opsQueue.push({ operation, errorHandler });
    } else {
      try {
        operation(this._sourceBuffer);
      } catch (e) {
        if (errorHandler) {
          errorHandler(this._sourceBuffer, e as Error);
        } else {
          this._eventBus.publish(EEvent.ERROR, { type: EErrorType.MEDIA_ERROR, details: e });
          this._logger.error(`Cannot recreate source buffer: ${(e as Error).toString()}, ${operation}`);
        }
      }
    }
  }

  public immediatelyRecreateSourceBuffer(parentMediaSource: MediaSource | null): void {
    try {
      if (!this._sourceBuffer || !parentMediaSource || ![...parentMediaSource.sourceBuffers].includes(this._sourceBuffer)) {
        return;
      }

      this._disarmSourceBufferStuckTimer();

      this._opsQueue = [];

      this._sourceBuffer.abort();
      this._sourceBuffer.remove(0, Number.POSITIVE_INFINITY);
    } catch (e) {
      this._logger.error(`Cannot recreate source buffer: ${(e as Error).toString()}`);
    }
  }

  private _recheckQueue(): void {
    if (!this._sourceBuffer || this._sourceBuffer.updating || !this._opsQueue.length) {
      return;
    }

    const opItem: TOpItem | undefined = this._opsQueue.shift();

    if (opItem) {
      try {
        opItem.operation(this._sourceBuffer);
      } catch (e) {
        if (opItem.errorHandler) {
          opItem.errorHandler?.(this._sourceBuffer, e as Error);
        } else {
          this._logger.error(`Cannot perform source buffer operation: ${(e as Error).toString()} ${opItem}`);
          this._eventBus.publish(EEvent.ERROR, { type: EErrorType.MEDIA_ERROR, details: e });
        }
      }
    }

    if (!this._sourceBuffer.updating && this._opsQueue.length) {
      this._recheckQueue();
    }
  }

  @bind
  private _onBufferReady(): void {
    this._recheckQueue();

    this._disarmSourceBufferStuckTimer();
  }

  @bind
  private _disarmSourceBufferStuckTimer(): void {
    if (this._sourceBufferStuckTimer) {
      clearTimeout(this._sourceBufferStuckTimer);
    }
  }
}
